import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { generateArtworkAlt } from "../../utils/altTagGenerator";
import { createSlug } from '../../utils/slugify';
import "./HomeMainSlider.css";

import { API_BASE_URL } from '../../config/api';
import { getoptionalAppAuthHeaders, isWebsiteLoggedIn } from "../../utils/optionalAppAuth";
import { handleImageFallback, withFallbackImage } from "../../utils/imageFallback";
import { addGuestCartItem, showOneArtworkAtATimeAlert } from "../../utils/guestCart";
import Swal from 'sweetalert2';
import SwiperCore, { EffectFade, Autoplay, Pagination, Navigation } from 'swiper';
import { trackEvent } from '../../utils/analytics';

SwiperCore.use([EffectFade, Autoplay, Pagination, Navigation]);

const formatGrade = (grade) => {
    if (!grade) return 'N/A';

    const value = String(grade).trim();

    if (/year/i.test(value)) {
        const year = parseInt(value);

        if (isNaN(year)) return value;

        const suffix =
            year === 1 ? 'st' :
                year === 2 ? 'nd' :
                    year === 3 ? 'rd' : 'th';

        return `${year}${suffix} Year`;
    }

    const g = parseInt(value);

    if (isNaN(g)) return value;

    const suffix =
        g === 1 ? 'st' :
            g === 2 ? 'nd' :
                g === 3 ? 'rd' : 'th';

    return `Class ${g}${suffix}`;
};

const showToast = (icon, title, text, timer = 2000) => {
    const Toast = Swal.mixin({
        toast: true,
        position: 'bottom',
        showConfirmButton: false,
        timer: timer,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.onmouseenter = Swal.stopTimer;
            toast.onmouseleave = Swal.resumeTimer;
            toast.style.marginBottom = '20px';
            toast.style.zIndex = '9999';
        }
    });

    Toast.fire({
        icon: icon,
        title: title,
        text: text
    });
};

const SkeletonMainSlide = () => (
    <div className="premium-slide premium-slide-skeleton">
        <div className="premium-overlay-gradient"></div>
        <div className="premium-bg-pattern"></div>
        <div className="premium-container">
            <div className="premium-showcase premium-showcase-skeleton">
                <div className="premium-stage-art">
                    <div className="skeleton-image premium-skeleton-art"></div>
                </div>
                <div className="premium-editorial-card premium-skeleton-card">
                    <div className="skeleton-line" style={{ width: '120px', height: '28px' }}></div>
                    <div className="skeleton-line" style={{ width: '88%', height: '64px' }}></div>
                    <div className="skeleton-line" style={{ width: '62%', height: '18px' }}></div>
                    <div className="skeleton-line" style={{ width: '92%', height: '68px' }}></div>
                </div>
                <div className="premium-price-dock premium-skeleton-dock">
                    <div className="skeleton-line" style={{ width: '220px', height: '40px' }}></div>
                    <div className="skeleton-buttons" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <div className="skeleton-line" style={{ width: '150px', height: '52px' }}></div>
                        <div className="skeleton-line" style={{ width: '150px', height: '52px' }}></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const SkeletonThumbSlide = () => (
    <div className="premium-thumb-card">
        <div className="skeleton-image" style={{ width: '100%', height: '160px', borderRadius: '18px 18px 0 0' }}></div>
        <div className="premium-thumb-content">
            <div className="skeleton-line" style={{ width: '80%', height: '16px', marginBottom: '8px' }}></div>
            <div className="skeleton-line" style={{ width: '60%', height: '12px', marginBottom: '10px' }}></div>
            <div className="skeleton-line" style={{ width: '50%', height: '20px' }}></div>
        </div>
    </div>
);

export default function HomeMainSlider() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [mainSwiper, setMainSwiper] = useState(null);
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    useEffect(() => {
        const fetchFeatured = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/server/website/artworks/featured`);
                const result = await response.json();
                if (response.ok && result?.success) {
                    setItems(result?.data?.artworks || []);
                } else {
                    setItems([]);
                }
            } catch (error) {
                console.error('Error fetching featured artworks:', error);
                setItems([]);
            } finally {
                setLoading(false);
            }
        };

        fetchFeatured();
    }, []);

    const slides = useMemo(() => {
        const limit = 5;

        const uniqueItems = [];
        const usedStudents = new Set();

        for (const item of items || []) {
            const studentId = String(item.student_id || '');

            if (!usedStudents.has(studentId)) {
                uniqueItems.push(item);
                usedStudents.add(studentId);
            }

            if (uniqueItems.length === limit) {
                break;
            }
        }

        if (uniqueItems.length < limit) {
            for (const item of items || []) {
                const alreadyAdded = uniqueItems.some(
                    (artwork) => String(artwork.id) === String(item.id)
                );

                if (!alreadyAdded) {
                    uniqueItems.push(item);
                }

                if (uniqueItems.length === limit) {
                    break;
                }
            }
        }

        return uniqueItems.map((item) => {
            const currentPrice = item.price;
            const originalPrice = item.original_price;

            const discountPercent = originalPrice > 0
                ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
                : 0;

            return {
                id: item.id,
                encodedId: btoa(item.id),
                slug: createSlug(
                    item.name,
                    item.student_name || item.student?.name || ''
                ),
                product_quantity: item.product_quantity,
                image: withFallbackImage(item?.image),
                title: item.name,
                schoolName: item.school_name || item.student?.school_name || item.student_name || '',
                category: item.category || 'Artwork',
                studentId: item.student_id || '',
                studentName: item.student_name || '',
                cityName: item.city_name || '',
                description: item.description || 'Handcrafted artwork by student creator.',
                student_tagline: item.student_tagline || 'Handcrafted artwork by student creator.',
                grade: item.grade,
                artworkCount: item.artwork_count || item.student?.artwork_count || 0,
                price: currentPrice,
                original_price: originalPrice,
                offer: item.display_markup_percentage || discountPercent,
                rating: Number(item?.average_rating || 0),
                ratingCount: Number(item?.ratings_count || 0)
            };
        });
    }, [items]);

    const handleAddToCart = async (product) => {
        trackEvent('add_to_cart', {
            currency: 'INR',
            value: product.price,
            items: [
                {
                    item_id: product.id,
                    item_name: product.title
                }
            ]
        });

        const productId = product.id;

        if (!productId) return;

        if (!isWebsiteLoggedIn()) {
            const guestCartResult = addGuestCartItem({
                product_id: productId,
                name: product.title,
                image: product.image,
                price: product.price
            }, 1);

            if (!guestCartResult.accepted) return;

            showToast('success', 'Added to Cart! ??', 'Product added to your cart successfully!');
            window.dispatchEvent(new Event('cartWishlistUpdated'));
            return;
        }

        try {
            const cartResponse = await fetch(`${API_BASE_URL}/server/website/cart`, {
                method: 'GET',
                headers: { ...getoptionalAppAuthHeaders() }
            });

            const cartResult = await cartResponse.json();

            if (!cartResponse.ok || !cartResult?.success) {
                throw new Error('Unable to fetch cart');
            }

            const existingItem = (cartResult?.data?.cart || []).find(
                (item) => String(item.product_id) === String(productId)
            );

            if (!existingItem && (cartResult?.data?.cart || []).length > 0) {
                showOneArtworkAtATimeAlert(() => navigate('/shop-cart'));
                return;
            }

            const nextQuantity = Number(existingItem?.quantity || 0) + 1;
            const endpoint = existingItem ? 'update' : 'add';
            const response = await fetch(`${API_BASE_URL}/server/website/cart/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getoptionalAppAuthHeaders()
                },
                body: JSON.stringify({
                    product_id: productId,
                    productId,
                    quantity: nextQuantity || 1
                })
            });

            const addData = await response.json();

            if (!response.ok || !addData.success) {
                throw new Error(addData.message || 'Failed to add to cart');
            }

            showToast('success', 'Added to Cart!', 'Product added to your cart successfully!');
            window.dispatchEvent(new Event('cartWishlistUpdated'));

            if (!existingItem && (cartResult?.data?.cart || []).length === 0) {
                navigate('/shop-checkout');
            }
        } catch (error) {
            showToast('error', 'Error', error.message || 'Unable to connect to server.', 3000);
        }
    };

    if (loading) {
        return (
            <div className="premium-slider-wrapper">
                <div className="premium-main-slider">
                    {[1, 2].map((_, index) => (
                        <SkeletonMainSlide key={`skeleton-main-${index}`} />
                    ))}
                </div>
                <div className="premium-thumbs-wrapper">
                    <div className="premium-thumbs-container">
                        {[1, 2, 3, 4].map((_, index) => (
                            <SkeletonThumbSlide key={`skeleton-thumb-${index}`} />
                        ))}
                    </div>
                </div>
                <style>{`
                    @keyframes shimmer {
                        0% { background-position: -200% 0; }
                        100% { background-position: 200% 0; }
                    }
                    .skeleton-line, .skeleton-image {
                        background: linear-gradient(90deg, rgba(224, 224, 224, 0.18) 25%, rgba(255, 255, 255, 0.28) 50%, rgba(224, 224, 224, 0.18) 75%);
                        background-size: 200% 100%;
                        animation: shimmer 1.5s infinite;
                        border-radius: 12px;
                    }
                `}</style>
            </div>
        );
    }

    if (!loading && slides.length === 0) {
        return (
            <div
                className="premium-empty-state"
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center"
                }}
            >
                <div className="premium-empty-content">
                    <div className="premium-empty-icon">🎨</div>
                    <h3>No Featured Artworks Available</h3>
                    <p>Check back soon for new creations from our talented student artists!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="premium-slider-wrapper">
            <div className="premium-main-slider">
                <Swiper
                    onSwiper={setMainSwiper}
                    className="premium-swiper"
                    speed={1000}
                    slidesPerView={1}
                    loop={true}
                    navigation={{
                        prevEl: navigationPrevRef.current,
                        nextEl: navigationNextRef.current,
                    }}
                    onBeforeInit={(swiper) => {
                        swiper.params.navigation.prevEl = navigationPrevRef.current;
                        swiper.params.navigation.nextEl = navigationNextRef.current;
                        swiper.navigation.init();
                        swiper.navigation.update();
                    }}
                    pagination={{
                        clickable: true,
                        dynamicBullets: false,
                    }}
                    onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                >
                    {slides.map((data, index) => (
                        <SwiperSlide key={data.id || index}>
                            <div className="premium-slide">
                                <div className="premium-overlay-gradient"></div>
                                <div className="premium-bg-pattern"></div>
                                <div className="premium-stage-orb premium-stage-orb-one"></div>
                                <div className="premium-stage-orb premium-stage-orb-two"></div>

                                <div className="premium-container">
                                    <div className="premium-showcase">
                                        <h1
                                            style={{
                                                position: "absolute",
                                                left: "-9999px",
                                                top: "auto",
                                                width: "1px",
                                                height: "1px",
                                            }}
                                        >
                                            Buy Handmade Gifts Online from Student Creators | ArtTokri
                                        </h1>

                                        <div className="premium-image-col premium-stage-art" data-aos="fade-left">
                                            <div className="premium-stage-panel premium-stage-panel-top"></div>
                                            <Link
                                                to={`/${data.slug}/p/${data.encodedId}`}
                                                className="premium-stage-link"
                                                target={window.innerWidth >= 992 ? "_blank" : ""}
                                                rel={window.innerWidth >= 992 ? "noopener noreferrer" : undefined}
                                                onClick={() => {
                                                    trackEvent('view_item', {
                                                        item_id: data.id,
                                                        item_name: data.title
                                                    });
                                                }}
                                            >
                                                <div className="premium-image-wrapper">
                                                    <div className="premium-image-glow"></div>
                                                    <img
                                                        src={data.image}
                                                        onError={handleImageFallback}
                                                        loading={index === 0 ? "eager" : "lazy"}
                                                        fetchPriority={index === 0 ? "high" : "auto"}
                                                        decoding="async"
                                                        alt={generateArtworkAlt({
                                                            title: data.title,
                                                            studentName: data.studentName,
                                                            cityName: data.cityName
                                                        })}
                                                        className="premium-image"
                                                    />
                                                    <div className="premium-image-badge">
                                                        <span>Featured</span>
                                                    </div>
                                                </div>
                                            </Link>
                                            <div className="premium-stage-panel premium-stage-panel-bottom"></div>
                                        </div>

                                        <div className="premium-info-rail">
                                            <div className="premium-content-col premium-editorial-card" data-aos="fade-right">
                                                <div className="premium-category-badge">
                                                    <span className="premium-category-icon">✨</span>
                                                    {data.category}
                                                </div>

                                                <h2 className="premium-title">
                                                    <Link
                                                        to={`/${data.slug}/p/${data.encodedId}`}
                                                        className="premium-title-link"
                                                        target={window.innerWidth >= 992 ? "_blank" : ""}
                                                        rel={window.innerWidth >= 992 ? "noopener noreferrer" : undefined}
                                                        onClick={() => {
                                                            trackEvent('view_item', {
                                                                item_id: data.id,
                                                                item_name: data.title
                                                            });
                                                        }}
                                                    >
                                                        {data.title}
                                                    </Link>
                                                </h2>

                                                <div className="premium-meta">
                                                    <Link to={`/student-profile/${data.studentId}`} className="premium-artist-link">
                                                        <span className="artist-avatar">🎨</span>
                                                        {data.schoolName || data.studentName || 'Student Artist'}
                                                    </Link>
                                                    <span className="premium-grade">
                                                        <span className="grade-icon">📚</span>
                                                        {formatGrade(data.grade)}
                                                    </span>
                                                </div>

                                                <p className="premium-description">{data.student_tagline}</p>
                                            </div>

                                            <div className="premium-price-dock" data-aos="fade-up">
                                                <div className="premium-price-section">
                                                    <span className="premium-current-price">₹{data.price.toLocaleString('en-IN')}</span>
                                                    {data.original_price > data.price && (
                                                        <>
                                                            <span className="premium-original-price">₹{data.original_price.toLocaleString('en-IN')}</span>
                                                            <span className="premium-discount-badge">{data.offer}% OFF</span>
                                                        </>
                                                    )}
                                                </div>

                                                <div className="premium-actions">
                                                    <button
                                                        className="premium-btn-primary"
                                                        onClick={() => handleAddToCart(data)}
                                                        disabled={data.product_quantity === 0}
                                                        style={{
                                                            opacity: data.product_quantity == 0 ? 0.6 : 1,
                                                            cursor: data.product_quantity == 0 ? 'not-allowed' : 'pointer'
                                                        }}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.3 5.1 16.3H17M17 13L17.3 15.3C17.4 15.8 17 16.3 16.5 16.3H8.5M7 18C7 18.6 7.4 19 8 19C8.6 19 9 18.6 9 18C9 17.4 8.6 17 8 17C7.4 17 7 17.4 7 18ZM16 18C16 18.6 16.4 19 17 19C17.6 19 18 18.6 18 18C18 17.4 17.6 17 17 17C16.4 17 16 17.4 16 18Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                        </svg>
                                                        {data.product_quantity == 0 ? 'Sold Out' : 'Buy Now'}
                                                    </button>
                                                    <Link
                                                        className="premium-btn-secondary"
                                                        to={`/${data.slug}/p/${data.encodedId}`}
                                                        target={window.innerWidth >= 992 ? "_blank" : ""}
                                                        rel={window.innerWidth >= 992 ? "noopener noreferrer" : undefined}
                                                        onClick={() => {
                                                            trackEvent('view_item', {
                                                                item_id: data.id,
                                                                item_name: data.title
                                                            });
                                                        }}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="1.5" />
                                                            <path d="M2.5 12C2.5 12 5.5 5 12 5C18.5 5 21.5 12 21.5 12C21.5 12 18.5 19 12 19C5.5 19 2.5 12 2.5 12Z" stroke="currentColor" strokeWidth="1.5" />
                                                        </svg>
                                                        Quick View
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>

                <button className="premium-nav-prev" ref={navigationPrevRef} aria-label="Previous featured artwork">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button className="premium-nav-next" ref={navigationNextRef} aria-label="Next featured artwork">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        </div>
    );
}
